import type { AppointmentStatus } from "@planerius/contracts"
import type { ReactNode } from "react"

import { cx } from "../lib/cx"
import { formatDayMonth, formatTimeOfDay } from "../lib/format"
import { plural } from "../lib/plural"
import { masterTintStyle } from "../theme/master-color"
import type { FeedRow, JournalListRow } from "./journal-list"
import { buildAllFeed, buildDayList } from "./journal-list"
import styles from "./journal-list.module.css"
import type { JournalColumn, JournalDayModel } from "./journal-model"
import { formatCountdown, formatDuration, formatMinute, formatRange } from "./journal-time"
import { statusVisual } from "./status-style"
import { useNowMinute, useSecondsNow } from "./use-journal-clock"

/**
 * День списком — журнал на телефоне (5-А1, 360).
 *
 * Сетка сюда не сжимается, она заменяется целиком (ТЗ §2.13). У администратора в зале
 * два вопроса: «что сейчас у салона» и «куда посадить пришедшего», поэтому лента
 * открывается ОБЩЕЙ — все мастера по времени, с подписью, чей это час. Вкладка
 * одного мастера остаётся: она и есть рабочий экран самого мастера.
 *
 * ВСЁ, ЧТО ВИДНО, — НАЖИМАЕТСЯ. Запись открывает карточку (требование ТЗ «открыть
 * запись за два касания»), свободный промежуток открывает форму на этом мастере и этом
 * времени, карантин занимается вручную. Строка, на которую смотришь и не можешь тронуть,
 * на телефоне читается как поломка: мыши с подсказками здесь нет.
 *
 * «ЗАПИСАТЬ» ВНИЗУ, В ЗОНЕ БОЛЬШОГО ПАЛЬЦА, и открывается на ВЫБРАННОМ мастере, а не
 * на первом в списке: администратор смотрит на день Ани и нажимает «Записать» — значит,
 * записывает к Ане.
 */

const STATUS_BADGE: Readonly<Record<AppointmentStatus, string>> = {
  pending: "statusPending",
  confirmed: "statusPending",
  in_progress: "statusRunning",
  completed: "statusCompleted",
  cancelled: "statusCancelled",
  no_show: "statusNoShow",
}

export function JournalDayList(props: {
  readonly model: JournalDayModel
  /** Чей день показан. null — общая лента «Все»: так экран и открывается. */
  readonly selectedEmployeeId: string | null
  readonly onSelectEmployee: (employeeId: string | null) => void
  /** Открыть карточку записи. Без обработчика лента только читается. */
  readonly onOpenCard?: ((cardId: string) => void) | undefined
  /** Свободное время и карантин: записать на этого мастера и эту минуту. */
  readonly onPickSlot?: ((employeeId: string | null, minute: number) => void) | undefined
  /** «Записать» в зоне большого пальца: открывает форму на выбранном мастере. */
  readonly onCreate?: ((employeeId: string | null) => void) | undefined
  /** Почему записать нельзя (нет связи, нет права). Кнопка остаётся видимой и объясняет. */
  readonly createDeniedReason?: string | null
}): ReactNode {
  const { model } = props
  const nowMinute = useNowMinute(model.timeZone, model.date)

  /*
   * ОДИН МАСТЕР — ЗНАЧИТ ЕГО ДЕНЬ, а не общая лента. Вкладок при одной колонке нет,
   * выбрать мастера нечем, и «Все» оказывалось не выбором, а тупиком: из общей ленты
   * выброшены промежутки и занятость, поэтому сольный салон переставал видеть своё
   * свободное время и не мог нажать на него, хотя пустой день прямо это предлагает.
   */
  const selected =
    props.selectedEmployeeId === null
      ? model.columns.length === 1
        ? (model.columns[0] ?? null)
        : null
      : (model.columns.find((item) => item.employee.id === props.selectedEmployeeId) ?? null)

  // Таймеры заводятся ДО любого возврата: порядок вызовов хуков обязан совпадать
  // на каждом рендере, а колонок может не быть — салон ещё не завёл мастеров.
  const hasHolds = model.columns.some((column) => column.holds.length > 0)
  const secondsNow = useSecondsNow(hasHolds)

  if (model.columns.length === 0) {
    return (
      <div className={styles["list"]}>
        <div className={styles["empty"]}>
          <p className={styles["emptyTitle"]}>Чтобы вести запись, добавьте хотя бы одного мастера</p>
          <p className={styles["emptyText"]}>
            Записи появятся в журнале, как только у мастера будут услуги и смена. Первые три
            шага занимают 10 минут.
          </p>
        </div>
      </div>
    )
  }

  const feed: readonly FeedRow[] =
    selected === null
      ? buildAllFeed(model, nowMinute)
      : buildDayList(selected, nowMinute).map((row) => ({
          row,
          employeeId: selected.employee.id,
          employeeName: selected.employee.displayName,
          employeeColor: selected.employee.color,
        }))

  const pickEmployeeId = selected?.employee.id ?? null

  return (
    <>
      {model.columns.length > 1 ? (
        <div className={styles["picker"]} role="tablist" aria-label="Мастер">
          <button
            type="button"
            role="tab"
            aria-selected={selected === null}
            className={cx(
              styles["pickerChip"],
              selected === null ? styles["pickerChipActive"] : undefined,
            )}
            onClick={() => {
              props.onSelectEmployee(null)
            }}
          >
            Все
          </button>
          {model.columns.map((item) => (
            <button
              key={item.employee.id}
              type="button"
              role="tab"
              aria-selected={item.employee.id === selected?.employee.id}
              className={cx(
                styles["pickerChip"],
                item.employee.id === selected?.employee.id ? styles["pickerChipActive"] : undefined,
              )}
              onClick={() => {
                props.onSelectEmployee(item.employee.id)
              }}
            >
              {item.employee.displayName}
            </button>
          ))}
        </div>
      ) : null}

      <div className={styles["shiftLine"]}>
        {selected === null ? allLine(model) : shiftLine(selected, model)}
      </div>

      {/* «Без мастера: 1» — не украшение: это клиент, который придёт, а мастера ему
          ещё не назначили. В общей ленте такие записи стоят на своём времени, поэтому
          строка только считает их и ведёт к первой. */}
      {selected === null && model.unassigned.length > 0 ? (
        <button
          type="button"
          className={styles["unassignedLine"]}
          onClick={() => {
            const first = model.unassigned[0]
            if (first !== undefined) props.onOpenCard?.(first.id)
          }}
        >
          Без мастера: {model.unassigned.length} · назначьте мастера
        </button>
      ) : null}

      <div className={styles["list"]}>
        {feed.length === 0 ? <EmptyDay model={model} column={selected} /> : null}
        {feed.map((item) => (
          <ListRow
            key={`${item.employeeId ?? "none"}:${rowKey(item.row)}`}
            item={item}
            showMaster={selected === null}
            nowMinute={nowMinute}
            secondsNow={secondsNow}
            timeZone={model.timeZone}
            onOpenCard={props.onOpenCard}
            onPickSlot={props.onPickSlot}
          />
        ))}
      </div>

      {/* Зона большого пальца. Кнопка видима всегда и объясняет отказ: выключенная
          кнопка с причиной честнее исчезнувшей (ТЗ §2.11.4). */}
      {props.onCreate === undefined && props.createDeniedReason == null ? null : (
        <div className={styles["thumbBar"]}>
          <button
            type="button"
            className={styles["thumbButton"]}
            disabled={props.createDeniedReason != null}
            title={props.createDeniedReason ?? undefined}
            onClick={() => {
              props.onCreate?.(pickEmployeeId)
            }}
          >
            {/*
              * В общей ленте мастера у записи не будет: она уйдёт в «Без мастера», и время
              * НЕ забронируется — занятости у записи без исполнителя не бывает. Сказать
              * это надо на самой кнопке: «Записать» в списке всего салона читается как
              * «записать к кому-нибудь», а получается «запись, которую ещё разбирать».
              */}
            {selected === null
              ? "Записать без мастера"
              : `Записать · ${selected.employee.displayName}`}
          </button>
          {props.createDeniedReason == null ? null : (
            <div className={styles["thumbDenied"]}>{props.createDeniedReason}</div>
          )}
        </div>
      )}
    </>
  )
}

/** Подпись общей ленты: день целиком одной строкой. */
function allLine(model: JournalDayModel): string {
  const live = model.summary.appointments
  const shifts = model.columns.filter((column) => column.workStartMinute !== null).length
  return `Записей: ${String(live)} · смены у ${String(shifts)} из ${String(model.columns.length)} · данные на ${formatTimeOfDay(model.generatedAt, model.timeZone)}`
}

function shiftLine(column: JournalColumn, model: JournalDayModel): string {
  const start = column.workStartMinute
  const end = column.workEndMinute
  const shift =
    start === null || end === null ? "Выходной" : `Смена ${formatRange(start, end)}`
  const live = column.cards.filter((card) => card.status !== "cancelled").length
  // Возраст данных показывается ВСЕГДА, а не только когда он велик (ТЗ §2.11.4):
  // подпись времени — единственное, что отделяет рабочий экран от вранья.
  return `${shift} · записей: ${String(live)} · данные на ${formatTimeOfDay(model.generatedAt, model.timeZone)}`
}

/**
 * Пустой день. Текст называет ДАТУ, а не «сегодня»: администратор листает дни вперёд,
 * и «Сегодня записей нет» на пятнице через неделю — прямая неправда (5-А1).
 */
function EmptyDay(props: {
  readonly model: JournalDayModel
  readonly column: JournalColumn | null
}): ReactNode {
  const { model, column } = props
  if (column !== null) {
    const hasShift = column.workStartMinute !== null
    return (
      <div className={styles["empty"]}>
        <p className={styles["emptyTitle"]}>
          {hasShift
            ? `Записей на ${dayLabel(model)} нет`
            : `На ${dayLabel(model)} у этого мастера выходной`}
        </p>
        <p className={styles["emptyText"]}>
          {hasShift
            ? "Время открыто для записи — клиенты могут записаться онлайн. Нажмите «Записать» или на свободное время."
            : "Смену ставит администратор. Как только она появится, здесь будет день."}
        </p>
      </div>
    )
  }

  const shifts = model.columns.filter((item) => item.workStartMinute !== null).length
  return (
    <div className={styles["empty"]}>
      <p className={styles["emptyTitle"]}>Записей на {dayLabel(model)} нет</p>
      <p className={styles["emptyText"]}>
        {shifts === 0
          ? "Смен в этот день нет ни у кого — поставьте смену, и время откроется для записи."
          : `Смены есть у ${String(shifts)} ${plural(shifts, "мастера", "мастеров", "мастеров")} · свободно ${formatDuration(model.summary.freeMinutes)}. Откройте мастера, чтобы выбрать время, или нажмите «Записать».`}
      </p>
    </div>
  )
}

/** «16 сентября» — без дня недели: он уже стоит в шапке журнала, и дважды его не надо. */
function dayLabel(model: JournalDayModel): string {
  return formatDayMonth(model.date, model.timeZone)
}

function ListRow(props: {
  readonly item: FeedRow
  readonly showMaster: boolean
  readonly nowMinute: number
  readonly secondsNow: number
  readonly timeZone: string
  readonly onOpenCard?: ((cardId: string) => void) | undefined
  readonly onPickSlot?: ((employeeId: string | null, minute: number) => void) | undefined
}): ReactNode {
  const { row } = props.item

  if (row.kind === "gap") {
    const body = (
      <>
        <span>{formatRange(row.startMinute, row.endMinute)}</span>
        <span>Свободно {formatDuration(row.endMinute - row.startMinute)}</span>
      </>
    )
    /*
     * Свободный промежуток — самая частая цель на телефоне: администратор ищет, куда
     * посадить человека, стоящего перед ним. Нажатие открывает форму на этом мастере
     * и на НАЧАЛЕ промежутка, а не «примерно там».
     */
    if (props.onPickSlot === undefined) {
      return <div className={styles["gap"]}>{body}</div>
    }
    return (
      <button
        type="button"
        className={cx(styles["gap"], styles["gapTappable"])}
        onClick={() => {
          props.onPickSlot?.(props.item.employeeId, row.startMinute)
        }}
      >
        {body}
      </button>
    )
  }

  if (row.kind === "busy") {
    return (
      <div className={styles["busyRow"]}>
        <span>{formatRange(row.block.startMinute, row.block.endMinute)}</span>
        <span>{row.block.label}</span>
      </div>
    )
  }

  if (row.kind === "hold") {
    const remaining = (Date.parse(row.hold.expiresAt) - props.secondsNow) / 1000
    const quarantine = row.hold.isQuarantine
    const subtitle = quarantine
      ? `В онлайн-запись вернётся в ${formatTimeOfDay(row.hold.expiresAt, props.timeZone)} · можно занять вручную`
      : remaining > 0
        ? `Освободится через ${formatCountdown(remaining)}`
        : "Время освобождается…"
    const body = (
      <>
        <span>
          {formatMinute(row.hold.startMinute)} · {row.hold.title}
          {props.showMaster ? ` · ${props.item.employeeName}` : ""}
        </span>
        <span className={styles["holdSub"]}>{subtitle}</span>
      </>
    )
    /*
     * КАРАНТИН В ЛЕНТЕ ТЕЛЕФОНА НАЖИМАЕТСЯ. Администратор с телефона в зале — тот самый
     * человек, которому освободившееся время надо занять быстрее онлайн-записи (2-2.4.1),
     * а лента до сих пор показывала ему это время как объявление. Чужое удержание
     * остаётся неподвижным: за ним живой человек вводит код.
     */
    if (quarantine && props.onPickSlot !== undefined) {
      return (
        <button
          type="button"
          className={cx(styles["holdRow"], styles["holdQuarantineRow"], styles["holdRowTappable"])}
          onClick={() => {
            props.onPickSlot?.(props.item.employeeId, row.hold.startMinute)
          }}
        >
          {body}
        </button>
      )
    }
    return (
      <div className={cx(styles["holdRow"], quarantine ? styles["holdQuarantineRow"] : undefined)}>
        {body}
      </div>
    )
  }

  const { card } = row
  const visual = statusVisual(card.status)
  const past = card.endMinute <= props.nowMinute || card.status === "completed"
  const unassigned = props.item.employeeId === null

  const body = (
    <>
      <div className={styles["rowTop"]}>
        <span className={cx(styles["time"], past ? styles["timePast"] : undefined)}>
          {card.timeLabel}
        </span>
        {props.showMaster ? (
          <span
            className={cx(
              styles["master"],
              unassigned ? styles["masterNone"] : "plnr-master-tint",
            )}
            style={
              props.item.employeeColor === null
                ? undefined
                : masterTintStyle(props.item.employeeColor)
            }
          >
            {props.item.employeeName}
          </span>
        ) : null}
        {row.isNext ? <span className={styles["nextBadge"]}>Следующий</span> : null}
        {visual.label === "" ? null : (
          <span className={cx(styles["statusBadge"], styles[STATUS_BADGE[card.status]])}>
            {visual.label}
          </span>
        )}
      </div>
      <div className={styles["rowBody"]}>
        <div className={cx(styles["client"], visual.strikeName ? styles["clientStruck"] : undefined)}>
          {card.clientName}
          {card.flags.includes("first") ? <span className={styles["firstVisit"]}>новый</span> : null}
        </div>
        <div className={styles["service"]}>
          {card.extraServices > 0
            ? `${card.serviceName} + ещё ${card.extraServices}`
            : card.serviceName}
          {unassigned ? " · назначьте мастера" : ""}
        </div>
        {card.flags.includes("pinned") ? (
          <div className={styles["pinnedNote"]}>! Есть закреплённая заметка о клиенте</div>
        ) : null}
      </div>
    </>
  )

  const className = cx(
    styles["row"],
    row.isNext ? styles["rowNext"] : undefined,
    past ? styles["rowPast"] : undefined,
    unassigned ? styles["rowUnassigned"] : undefined,
  )

  /*
   * ЗАПИСЬ ОТКРЫВАЕТСЯ ОДНИМ КАСАНИЕМ. До этого карточка на телефоне была текстом:
   * чтобы отметить приход или отменить визит, администратор искал ту же запись
   * в настольной сетке. Обещание ТЗ «открыть запись за два касания» не выполнялось.
   */
  if (props.onOpenCard === undefined) {
    return <div className={className}>{body}</div>
  }
  return (
    <button
      type="button"
      className={cx(className, styles["rowTappable"])}
      onClick={() => {
        props.onOpenCard?.(card.id)
      }}
    >
      {body}
    </button>
  )
}

function rowKey(row: JournalListRow): string {
  switch (row.kind) {
    case "appointment":
      return `a:${row.card.id}`
    case "busy":
      return `b:${row.block.id}`
    case "hold":
      return `h:${row.hold.id}`
    case "gap":
      return `g:${String(row.startMinute)}`
  }
}
